import { NetworkStatus } from '@apollo/client';
import { Flex } from '@chakra-ui/layout';
import { Divider, Skeleton } from '@chakra-ui/react';
import React from 'react';
import { Navigate } from 'react-router';

import { EventsTable as LegacyRouteBase } from './legacy/EventsTable';
import { RouteErrorBoundary } from '~/contexts/MonitoringProvider';
import {
  PermissionGuard,
  PermissionsFn,
  usePermissionGuard,
} from '~/core/PermissionGuard';
import * as CreditFacilitiesEntityConfig from '~/core/routes/credit-facilities/EntityConfig';
import * as DeliverablesEntityConfig from '~/core/routes/deliverables/EntityConfig';
import * as FundingRequestsEntityConfig from '~/core/routes/funding-requests/EntityConfig';
import * as InvoicesEntityConfig from '~/core/routes/invoices/EntityConfig';
import * as LimitedPartnersEntityConfig from '~/core/routes/limited-partners/EntityConfig';
import * as UsersEntityConfig from '~/core/routes/users/EntityConfig';
import * as ValleyBankLoanAccountsEntityConfig from '~/core/routes/valley-bank/EntityConfig';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { EventFeed } from '~/packages/event-logs/EventFeed';
import { EventLogExportButton } from '~/packages/event-logs/EventLogExportButton';
import {
  EventLogDateRangeFilter,
  EventLogFiltersProvider,
  EventLogTypeFilter,
  FilterContainer,
  useEventLogFilters,
} from '~/packages/event-logs/EventLogFilters';
import { eventLogTypeToDomain } from '~/packages/event-logs/helpers';
import {
  useEventLogQueryFilteredParams,
  useEventLogs,
} from '~/packages/event-logs/hooks/useEventLogs';

// Fragments
const RouteBase = () => {
  const allowedEventTypes = useEventLogFilters((s) => s.allowedEventTypes);
  const filteredParams = useEventLogQueryFilteredParams();
  const eventLogsQuery = useEventLogs({
    variables: { params: filteredParams },
    notifyOnNetworkStatusChange: true,
  });
  const eventLogs = eventLogsQuery.data?.eventLogs ?? {
    __typename: 'EventLogPaginatedFeed',
    hasMore: false,
    items: [],
  };
  if (eventLogsQuery.error) throw eventLogsQuery.error;
  const logDomains = eventLogTypeToDomain(allowedEventTypes);
  return (
    <>
      <Typography variant="page-title">Event Log</Typography>
      <FilterContainer>
        <EventLogExportButton logs={eventLogs.items} />
        <Divider orientation="vertical" height="auto" />
        <EventLogTypeFilter />
        <EventLogDateRangeFilter />
        <UsersEntityConfig.EntityFilter />

        {(logDomains.includes('deliverable') ||
          logDomains.includes('fundingRequest')) && (
          <CreditFacilitiesEntityConfig.EntityFilter />
        )}
        {logDomains.includes('fundingRequest') && (
          <FundingRequestsEntityConfig.EntityFilter />
        )}
        {logDomains.includes('invoice') && (
          <InvoicesEntityConfig.EntityFilter />
        )}
        {logDomains.includes('limitedPartner') && (
          <LimitedPartnersEntityConfig.EntityFilter />
        )}
        {logDomains.includes('deliverable') && (
          <DeliverablesEntityConfig.EntityFilter />
        )}
        {logDomains.includes('valleyBank_loanAccount') && (
          <ValleyBankLoanAccountsEntityConfig.EntityFilter />
        )}
      </FilterContainer>

      <Flex
        direction="column"
        gap="0.75rem"
        flexGrow={1}
        overflowY="auto"
        paddingBlock="1rem"
      >
        {/* Initial load denotation */}
        {eventLogsQuery.networkStatus === NetworkStatus.loading ? (
          <Skeleton height="3rem" />
        ) : (
          <EventFeed
            logs={eventLogs.items}
            direction="newest-first"
            showAllCaughtUp={false}
            hasMore={eventLogs.hasMore}
            fetchMore={(before) => {
              eventLogsQuery.fetchMore({
                variables: { params: { ...filteredParams, before } },
              });
            }}
            isFetchingMore={
              eventLogsQuery.networkStatus === NetworkStatus.fetchMore
            }
          />
        )}
      </Flex>
    </>
  );
};

const RouteBaseProviders = ({ children }: { children: React.ReactNode }) => {
  const allowedEventTypes = [
    CreditFacilitiesEntityConfig.EVENT_LOG_TYPES,
    usePermissionGuard(DeliverablesEntityConfig.permissions) === 'allow'
      ? DeliverablesEntityConfig.EVENT_LOG_TYPES
      : [],
    usePermissionGuard(FundingRequestsEntityConfig.permissions) === 'allow'
      ? FundingRequestsEntityConfig.EVENT_LOG_TYPES
      : [],
    usePermissionGuard(InvoicesEntityConfig.permissions) === 'allow'
      ? InvoicesEntityConfig.EVENT_LOG_TYPES
      : [],
    usePermissionGuard(LimitedPartnersEntityConfig.permissions) === 'allow'
      ? LimitedPartnersEntityConfig.EVENT_LOG_TYPES
      : [],
    usePermissionGuard(ValleyBankLoanAccountsEntityConfig.permissions) ===
    'allow'
      ? ValleyBankLoanAccountsEntityConfig.EVENT_LOG_TYPES
      : [],
    UsersEntityConfig.EVENT_LOG_TYPES,
  ].flat();

  const partialInitialValues: React.ComponentProps<
    typeof EventLogFiltersProvider
  >['partialInitialValues'] = { allowedEventTypes };

  return (
    <EventLogFiltersProvider partialInitialValues={partialInitialValues}>
      {children}
    </EventLogFiltersProvider>
  );
};

// Exports
export const permissions: PermissionsFn = (context) => {
  if (context.user.organization.slug === 'valleyBank') return 'deny';
  return context.flags.productsEventsLogPermissions;
};
export const Route = () => (
  <PermissionGuard
    permissions={permissions}
    fallback={<Navigate to="/" replace />}
  >
    <RouteErrorBoundary
      fallback={({ message, showErrorToast }) => {
        showErrorToast(message);
        return <Navigate to="/" />;
      }}
    >
      <Flex
        direction="column"
        padding={['1.5rem', '1.5rem', '1.5rem', '2.5rem']}
      >
        <PermissionGuard
          permissions={(ctx) => (ctx.flags.enableEventLogV2 ? 'allow' : 'deny')}
          fallback={<LegacyRouteBase />}
        >
          <RouteBaseProviders>
            <RouteBase />
          </RouteBaseProviders>
        </PermissionGuard>
      </Flex>
    </RouteErrorBoundary>
  </PermissionGuard>
);
